import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Typography,
} from '@mui/material';
import { Search, SearchOff } from '@mui/icons-material';
import SectionTitle from '../components/SectionTitle';
import EntryCard from '../components/EntryCard';
import AddressGuide from '../components/AddressGuide';
import { getZip } from '../services/api';
import { formatPostal } from '../utils/address';
import { useDataLastModified } from '../hooks/useDataLastModified';
import { t } from '../i18n';
import { pushEvent } from '../utils/analytics';

const PostalSearchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const zipParam = searchParams.get('zip') || '';
  const updated = useDataLastModified();

  const [code, setCode] = useState(zipParam);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Keep the input in sync when the URL changes (deep link / nav from another page).
  useEffect(() => {
    setCode(zipParam);
  }, [zipParam]);

  const runSearch = useCallback(async (rawCode) => {
    const clean = rawCode.replace(/[-\s]/g, '');
    if (!clean) return;
    setLoading(true);
    setError(null);
    setData(null);
    pushEvent('postal_search', { postal_code: clean });
    try {
      const res = await getZip(clean);
      setData(res);
      pushEvent('result_count', { page: 'postal', count: res.entries?.length || 0 });
    } catch (err) {
      setError(err.notFound ? 'not_found' : 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-run whenever the ?zip= param is present / changes.
  useEffect(() => {
    if (zipParam) {
      runSearch(zipParam);
    } else {
      setData(null);
      setError(null);
    }
  }, [zipParam, runSearch]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const clean = code.replace(/[-\s]/g, '');
    if (!clean) return;
    if (clean === zipParam) {
      runSearch(clean); // same code: re-run explicitly (param won't change)
    } else {
      setSearchParams({ zip: clean });
    }
  };

  return (
    <Box>
      <SectionTitle emoji="📮">{t('Postal Code Search', '郵便番号検索')}</SectionTitle>

      <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', gap: 1, alignItems: 'flex-end', flexWrap: 'wrap', mb: 1 }}>
        <TextField
          color="secondary"
          variant="standard"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          label={t('Postal Code', '郵便番号')}
          placeholder={t('e.g. 100-0001 or 1000001', '例: 100-0001 または 1000001')}
          inputProps={{ maxLength: 8, inputMode: 'numeric' }}
          sx={{ width: { xs: '100%', sm: 260 } }}
        />
        <Button type="submit" variant="contained" color="secondary" startIcon={<Search />} sx={{ px: 4 }}>
          {t('Search', '検索')}
        </Button>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
          <CircularProgress size={36} />
          <Typography sx={{ ml: 2 }}>{t('Searching…', '検索中…')}</Typography>
        </Box>
      )}

      {error === 'not_found' && (
        <Alert severity="warning" icon={<SearchOff />} sx={{ mt: 2 }}>
          {t(
            'Postal code not found. Please check the number and try again.',
            '郵便番号が見つかりません。番号を確認して、もう一度お試しください。'
          )}
        </Alert>
      )}
      {error === 'error' && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {t(
            'Something went wrong. Please try again later.',
            'エラーが発生しました。しばらくしてからもう一度お試しください。'
          )}
        </Alert>
      )}

      {data && !loading && (
        <Box sx={{ mt: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <Typography variant="subtitle1">
              <Box component="span" fontWeight="bold">
                〒{formatPostal(data.postal_code)}
              </Box>{' '}
              <Box component="span" color="text.secondary">
                ·{' '}
                {t(
                  `${data.entries.length} ${data.entries.length === 1 ? 'entry' : 'entries'} found`,
                  `${data.entries.length} 件`
                )}
              </Box>
            </Typography>
            {updated && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                {t('Data updated: ', 'データ更新日: ')}
                {updated}
              </Typography>
            )}
          </Box>
          {data.entries.map((entry, i) => (
            <EntryCard
              key={i}
              entry={entry}
              postalCode={data.postal_code}
              index={i}
              total={data.entries.length}
            />
          ))}
        </Box>
      )}

      {/* Always visible — the guide/example stays below the result (and during a search). */}
      <AddressGuide />

      {/* Persistent advisory — stays visible before and after a search. */}
      <Alert severity="info" variant="outlined" sx={{ mt: 4 }}>
        {t(
          'Business (大口事業所) results include a company name. Its English form is machine-romanized and may be inaccurate — when sending mail, also include the original Japanese company name shown in the result.',
          '事業所（大口事業所）の結果には会社名が含まれます。英語表記は自動変換のため正確でない場合があります。郵送の際は、検索結果に表示される日本語の会社名も併せてご記入ください。'
        )}
      </Alert>
    </Box>
  );
};

export default PostalSearchPage;
